import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import DocumentUpload from '../../common/DocumentUpload';
import { FormService } from '../../../services/patientmr/forms.service';
import type { FormRecordDto, FormDocumentDto } from '../../../dtos/patientmr/forms.dto';
import { POCCertificationsService } from '../../../services/pocCertifications.service';
import SubmitterCertificationSection from './SubmitterCertificationSection';
import { F00070_ACK_INSTRUCTION } from '../../../constants/tmhpFormText';
import { isApiError } from '../../../dtos/common/api.dto';
import PrintedNameField from './PrintedNameField';

interface CCPPAReauthFormProps {
  isReadOnly?: boolean;
  initialData?: Record<string, any>;
  onDataChange?: (data: any) => void;
  patientId?: string;
  formRecord?: FormRecordDto;
  onFormRecordChange?: (record: FormRecordDto) => void;
  /** Physician fields flagged by a blocked Submit / Generate PDF (client check or server 422). */
  fieldErrors?: ReauthPhysicianErrors;
}

const CollapsibleSection: React.FC<{
  title: string; sectionKey: string;
  collapsed: boolean; onToggle: () => void;
  children: React.ReactNode; colorClass?: string;
}> = ({ title, collapsed, onToggle, children, colorClass = 'border-slate-200 bg-slate-50' }) => (
  <div className={`rounded-lg border ${colorClass} shadow-sm mb-4`}>
    <div className="flex items-center justify-between cursor-pointer px-4 py-3 hover:bg-slate-100 rounded-t-lg transition-colors" onClick={onToggle}>
      <h4 className="text-base font-semibold text-gray-900 text-left">{title}</h4>
      {collapsed ? <ChevronDown size={16} className="text-gray-500 shrink-0" /> : <ChevronUp size={16} className="text-gray-500 shrink-0" />}
    </div>
    <div className={`px-4 pb-4 pt-2 ${collapsed ? 'hidden' : 'block'}`}>{children}</div>
  </div>
);

const RequiredMark: React.FC = () => <span className="text-red-500 ml-0.5">*</span>;

const inputClass =
  'w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500 text-sm text-left';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1 text-left';
/**
 * The six official F00070 criteria. "All required acknowledgments must be signed and dated." is an
 * instruction on the form, not a criterion (O-6). `criteria[]` is stored by index; a legacy
 * 7-entry array still reads correctly (indices 0-5; the old 7th entry is ignored).
 */
export const CRITERIA = [
  'Client has received PDN services for at least 3 months.',
  'Client has had no new significant diagnosis, treatment, illness/injury or hospitalization in at least 6 months that would be expected to affect the need for PDN services.',
  "Client's physician and client/parent/guardian do not anticipate any significant changes in the client's condition for the requested authorization period.",
  'The nurse provider will ensure that a new physician plan of care is obtained within 30 calendar days of the authorization expiration date and will be maintained with the client\'s record.',
  'The nurse provider will advise TMHP-CCP of any significant changes in the client\'s condition, treatments or physician orders which occur during the authorization period if the number of PDN hours needs to change.',
  "The client's physician, client/parent/guardian, and nurse provider understand that the authorization may be changed during the authorization period if the client's condition or skilled needs change significantly.",
];

/** Stored criteria (any length) -> exactly six booleans. */
export function readCriteria(value: unknown): boolean[] {
  const arr = Array.isArray(value) ? value : [];
  return CRITERIA.map((_, i) => arr[i] === true);
}

/**
 * F00070 requesting physician NPI: 10 digits with a valid check digit (Luhn over the prefix
 * 80840 + the NPI). Mirrors the server's isValidNpi.
 */
export function isValidPhysicianNpi(npi: string): boolean {
  const s = npi.trim();
  if (!/^\d{10}$/.test(s)) return false;
  const digits = `80840${s}`;
  let sum = 0;
  for (let i = digits.length - 1, dbl = false; i >= 0; i -= 1, dbl = !dbl) {
    let d = Number(digits[i]);
    if (dbl) { d *= 2; if (d > 9) d -= 9; }
    sum += d;
  }
  return sum % 10 === 0;
}

export type ReauthPhysicianField = 'physicianPrintedName' | 'physicianNpi';
export type ReauthPhysicianErrors = Partial<Record<ReauthPhysicianField, string>>;

/** Same messages as the server's validateReauthPhysician (422 REAUTH_CRITERIA_INCOMPLETE). */
export const REAUTH_PHYSICIAN_MESSAGES = {
  nameRequired: "Requesting physician's printed name is required",
  npiRequired: 'Requesting physician NPI is required',
  npiInvalid: 'Requesting physician NPI must be a valid 10-digit NPI',
} as const;

/** Physician name and NPI are mandatory at Submit / Generate PDF (drafts may save blanks). */
export function getReauthPhysicianErrors(data: Record<string, unknown>): ReauthPhysicianErrors {
  const errors: ReauthPhysicianErrors = {};
  if (!String(data.physicianPrintedName ?? '').trim()) errors.physicianPrintedName = REAUTH_PHYSICIAN_MESSAGES.nameRequired;
  const npi = String(data.physicianNpi ?? '').trim();
  if (!npi) errors.physicianNpi = REAUTH_PHYSICIAN_MESSAGES.npiRequired;
  else if (!isValidPhysicianNpi(npi)) errors.physicianNpi = REAUTH_PHYSICIAN_MESSAGES.npiInvalid;
  return errors;
}

/**
 * Client-side Submit / Generate PDF gate. Returns the server's 422 wording (criteria + physician)
 * and the flagged fields, or null when nothing blocks.
 */
export function getReauthBlockingIssues(data: Record<string, unknown>): { message: string; fieldErrors: ReauthPhysicianErrors } | null {
  const parts: string[] = [];
  const unchecked = readCriteria(data.criteria).filter(c => !c).length;
  if (unchecked) parts.push(`${unchecked} eligibility criteria not checked`);
  const fieldErrors = getReauthPhysicianErrors(data);
  (['physicianPrintedName', 'physicianNpi'] as const).forEach(f => { if (fieldErrors[f]) parts.push(fieldErrors[f] as string); });
  if (!parts.length) return null;
  return { message: `F00070 6-month authorization is not eligible: ${parts.join('; ')}`, fieldErrors };
}

/** Maps a server 422 REAUTH_CRITERIA_INCOMPLETE `details.missingFields` list to field errors. */
export function reauthFieldErrorsFromError(error: unknown): ReauthPhysicianErrors | null {
  if (!isApiError(error) || error.domainCode !== 'REAUTH_CRITERIA_INCOMPLETE') return null;
  const list = error.details?.missingFields;
  const errors: ReauthPhysicianErrors = {};
  if (Array.isArray(list)) {
    list.forEach((item: unknown) => {
      const { field, message } = (item ?? {}) as { field?: unknown; message?: unknown };
      if ((field === 'physicianPrintedName' || field === 'physicianNpi') && typeof message === 'string') errors[field] = message;
    });
  }
  return errors;
}

const CCPPAReauthForm: React.FC<CCPPAReauthFormProps> = ({ isReadOnly = false, initialData = {}, onDataChange, patientId, formRecord, onFormRecordChange, fieldErrors }) => {
  const [form, setForm] = useState<Record<string, any>>({
    clientName: '',
    medicaidNumber: '',
    date: '',
    ackRead: false,
    sigClientGuardian: '',
    sigDateClientGuardian: '',
    briefStatement: '',
    discussedWithClient: false,
    sigNurseProvider: '',
    sigDateNurseProvider: '',
    sigPhysician: '',
    sigDatePhysician: '',
    physicianPrintedName: '',
    physicianNpi: '',
    physicianPhone: '',
    physicianFax: '',
    physicianStreet: '',
    physicianCity: '',
    physicianState: '',
    physicianZip: '',
    submitterAgreed: false,
    submitterName: '',
    submitterTitle: '',
    submitterDate: '',
    ...initialData,
    criteria: readCriteria(initialData.criteria),
  });

  // Prefill the requesting physician from the latest POC certification that names one (new forms only)
  useEffect(() => {
    if (formRecord !== undefined || !patientId || isReadOnly) return;
    let cancelled = false;
    POCCertificationsService.listPOCCertifications(patientId)
      .then(res => {
        if (cancelled) return;
        const certs = [...(res?.certifications || [])]
          .filter(c => c.physicianName || c.physicianNpi)
          .sort((a, b) => String(b.certificationPeriodStart || '').localeCompare(String(a.certificationPeriodStart || '')));
        const cert = certs[0];
        if (!cert) return;
        const from: Record<string, string | undefined> = {
          physicianPrintedName: cert.physicianName,
          physicianNpi: cert.physicianNpi,
          physicianPhone: cert.physicianPhone,
          physicianFax: cert.physicianFax,
          physicianStreet: cert.physicianAddress?.street,
          physicianCity: cert.physicianAddress?.city,
          physicianState: cert.physicianAddress?.state,
          physicianZip: cert.physicianAddress?.zip,
        };
        setForm(prev => {
          const next = { ...prev };
          Object.entries(from).forEach(([k, v]) => { if (v && !String(prev[k] ?? '').trim()) next[k] = v; });
          return next;
        });
      })
      .catch(err => console.error('[CCPPAReauthForm] Physician prefill failed:', err instanceof Error ? err.message : 'unknown'));
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onDataChange?.(form);
  }, [form, onDataChange]);

  const set = (field: string, value: any) => {
    if (isReadOnly) return;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const setCriteria = (index: number, checked: boolean) => {
    if (isReadOnly) return;
    setForm(prev => {
      const criteria = readCriteria(prev.criteria);
      criteria[index] = checked;
      return { ...prev, criteria };
    });
  };

  const allCriteriaChecked = readCriteria(form.criteria).every(Boolean);
  // Live physician errors: a bad NPI shows as soon as it is typed; "required" shows once a
  // Submit / Generate PDF was blocked for that field, and clears as soon as it is fixed.
  const physicianErrors = getReauthPhysicianErrors(form);
  const npiTyped = String(form.physicianNpi ?? '').trim() !== '';
  const nameError = !isReadOnly && fieldErrors?.physicianPrintedName ? physicianErrors.physicianPrintedName : undefined;
  const npiError = !isReadOnly && (fieldErrors?.physicianNpi || npiTyped) ? physicianErrors.physicianNpi : undefined;

  const [collapsedSections, setCollapsedSections] = useState<Record<string,boolean>>({});
  const toggleSection = (k: string) => setCollapsedSections(p => ({...p, [k]: !p[k]}));

  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);


  const handleFilesSelected = (files: File[]) => {
    if (formRecord?.status !== 'COMPLETE') {
      setActionError('Document upload is only available when the form is in Complete status.');
      return;
    }
    setActionError(null);
    setStagedFiles(prev => [...prev, ...files]);
  };

  const handleRemoveStagedFile = (index: number) => {
    setStagedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadStagedFiles = async () => {
    if (!stagedFiles.length || !patientId || !formRecord) return;
    setActionError(null);
    setUploading(true);
    try {
      let updatedForm = formRecord;
      for (const file of stagedFiles) {
        updatedForm = await FormService.addDocument(patientId, formRecord.formId, file);
      }
      setStagedFiles([]);
      onFormRecordChange?.(updatedForm);
    } catch (err) {
      console.error('Upload failed:', err);
      setActionError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadDocument = async (doc: FormDocumentDto) => {
    if (!patientId || !formRecord) return;
    setActionError(null);
    try {
      const result = await FormService.getDocumentUrl(patientId, formRecord.formId, doc.id);
      window.open(result.url, '_blank');
    } catch (err) {
      console.error('Download failed:', err);
      setActionError(err instanceof Error ? err.message : 'Download failed. Please try again.');
    }
  };

  const handleDeleteDocument = async (doc: FormDocumentDto) => {
    if (!patientId || !formRecord) return;
    setActionError(null);
    try {
      setDeletingDocId(doc.id);
      const updatedForm = await FormService.deleteDocument(patientId, formRecord.formId, doc.id);
      onFormRecordChange?.(updatedForm);
    } catch (err) {
      console.error('Delete failed:', err);
      setActionError(err instanceof Error ? err.message : 'Delete failed. Please try again.');
    } finally {
      setDeletingDocId(null);
    }
  };

  return (
    <div className="space-y-6 text-left">
      <div className="mb-4">
        <p className="text-xs text-gray-500 uppercase tracking-wide text-left">Form Code: F00070</p>
        <p className="text-sm text-gray-600 text-left">CCP Prior Authorization — Private Duty Nursing 6-Month Authorization</p>
      </div>

      <SubmitterCertificationSection formCode="F00070" data={form} onChange={set} isReadOnly={isReadOnly} />

      {/* Client Information */}
      <CollapsibleSection
        title="Client Information"
        sectionKey="client-info"
        collapsed={!!collapsedSections['client-info']}
        onToggle={() => toggleSection('client-info')}
        colorClass="border-blue-200 bg-blue-50"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>Client Name<RequiredMark /></label>
            <input type="text" value={form.clientName} onChange={e => set('clientName', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Medicaid Number<RequiredMark /></label>
            <input type="text" value={form.medicaidNumber} onChange={e => set('medicaidNumber', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Date</label>
            <input type="date" value={form.date} onChange={e => set('date', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>
      </CollapsibleSection>

      {/* 6-Month Authorization Criteria */}
      <CollapsibleSection
        title="6-Month Authorization Criteria"
        sectionKey="criteria"
        collapsed={!!collapsedSections['criteria']}
        onToggle={() => toggleSection('criteria')}
        colorClass="border-amber-200 bg-amber-50"
      >
        <p className="text-sm text-gray-600 mb-3 text-left">
          The following criteria must be met before seeking a 6-month authorization of private duty nursing (PDN) services. Remember that authorization is a condition for reimbursement; it is not a guarantee. Each nurse provider should verify the continued Medicaid coverage for each client for each month of service.
        </p>
        {!allCriteriaChecked && !isReadOnly && (
          <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-700">
            All criteria must be checked before submission.
          </div>
        )}
        <div className="space-y-3">
          {CRITERIA.map((text, idx) => (
            <label key={idx} className="flex items-start gap-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={readCriteria(form.criteria)[idx]}
                onChange={e => setCriteria(idx, e.target.checked)}
                disabled={isReadOnly}
                className="mt-0.5 rounded border-gray-300 flex-shrink-0"
              />
              <span>{text}</span>
            </label>
          ))}
        </div>
        <p className="mt-4 text-sm font-semibold text-gray-800 text-center">{F00070_ACK_INSTRUCTION}</p>
      </CollapsibleSection>

      {/* Client/Guardian Acknowledgement */}
      <CollapsibleSection
        title="Client/Guardian Acknowledgement"
        sectionKey="client-acknowledgement"
        collapsed={!!collapsedSections['client-acknowledgement']}
        onToggle={() => toggleSection('client-acknowledgement')}
        colorClass="border-indigo-200 bg-indigo-50"
      >
        <label className="flex items-start gap-3 text-sm text-gray-700 mb-4">
          <input
            type="checkbox"
            checked={!!form.ackRead}
            onChange={e => set('ackRead', e.target.checked)}
            disabled={isReadOnly}
            className="mt-0.5 rounded border-gray-300 flex-shrink-0"
          />
          <span>I have read and understand the above information.</span>
        </label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <PrintedNameField signer="Client/Parent/Guardian" value={form.sigClientGuardian} onChange={v => set('sigClientGuardian', v)} readOnly={isReadOnly} inputClassName={inputClass} labelClassName={labelClass} />
          </div>
          <div>
            <label className={labelClass}>Date</label>
            <input type="date" value={form.sigDateClientGuardian} onChange={e => set('sigDateClientGuardian', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>
      </CollapsibleSection>

      {/* Brief Statement of Medical Necessity */}
      <CollapsibleSection
        title="Brief Statement of Medical Necessity"
        sectionKey="brief-statement"
        collapsed={!!collapsedSections['brief-statement']}
        onToggle={() => toggleSection('brief-statement')}
        colorClass="border-violet-200 bg-violet-50"
      >
        <label className={labelClass}>
          Brief statement of why a maximum 6-month recertification is appropriate for this client<RequiredMark />
        </label>
        <textarea
          rows={5}
          value={form.briefStatement}
          onChange={e => set('briefStatement', e.target.value)}
          readOnly={isReadOnly}
          className={inputClass}
          placeholder="Provide clinical justification for 6-month recertification..."
        />
      </CollapsibleSection>

      {/* Nurse Provider Section */}
      <CollapsibleSection
        title="Nurse Provider Section"
        sectionKey="nurse-provider"
        collapsed={!!collapsedSections['nurse-provider']}
        onToggle={() => toggleSection('nurse-provider')}
        colorClass="border-green-200 bg-green-50"
      >
        <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-600">
          I have discussed the above information with the client/parent/guardian.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <PrintedNameField signer="Nurse Provider" value={form.sigNurseProvider} onChange={v => set('sigNurseProvider', v)} readOnly={isReadOnly} inputClassName={inputClass} labelClassName={labelClass} />
          </div>
          <div>
            <label className={labelClass}>Date</label>
            <input type="date" value={form.sigDateNurseProvider} onChange={e => set('sigDateNurseProvider', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>
      </CollapsibleSection>

      {/* Physician Certification */}
      <CollapsibleSection
        title="Physician Certification"
        sectionKey="physician"
        collapsed={!!collapsedSections['physician']}
        onToggle={() => toggleSection('physician')}
        colorClass="border-red-200 bg-red-50"
      >
        <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-600">
          The above services are medically necessary, the client's condition is stable and this request supports the client's health and safety needs.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <PrintedNameField signer="Client's Physician" value={form.sigPhysician} onChange={v => set('sigPhysician', v)} readOnly={isReadOnly} inputClassName={inputClass} labelClassName={labelClass} />
          </div>
          <div>
            <label className={labelClass}>Date</label>
            <input type="date" value={form.sigDatePhysician} onChange={e => set('sigDatePhysician', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass} htmlFor="f00070-physician-name">Requesting physician's printed name<RequiredMark /></label>
            <input id="f00070-physician-name" type="text" required aria-required="true" value={form.physicianPrintedName} onChange={e => set('physicianPrintedName', e.target.value)} readOnly={isReadOnly} className={inputClass} aria-invalid={!!nameError} aria-describedby={nameError ? 'f00070-physician-name-error' : undefined} />
            {nameError && <p id="f00070-physician-name-error" className="mt-1 text-xs text-red-600 text-left" role="alert">{nameError}</p>}
          </div>
          <div>
            <label className={labelClass} htmlFor="f00070-physician-npi">NPI<RequiredMark /></label>
            <input id="f00070-physician-npi" type="text" inputMode="numeric" maxLength={10} required aria-required="true" value={form.physicianNpi} onChange={e => set('physicianNpi', e.target.value.replace(/\D/g, ''))} readOnly={isReadOnly} className={inputClass} aria-invalid={!!npiError} aria-describedby={npiError ? 'f00070-physician-npi-error' : undefined} />
            {npiError && <p id="f00070-physician-npi-error" className="mt-1 text-xs text-red-600 text-left" role="alert">{npiError}</p>}
          </div>
          <div>
            <label className={labelClass} htmlFor="f00070-physician-phone">Telephone</label>
            <input id="f00070-physician-phone" type="tel" value={form.physicianPhone} onChange={e => set('physicianPhone', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass} htmlFor="f00070-physician-fax">Fax</label>
            <input id="f00070-physician-fax" type="tel" value={form.physicianFax} onChange={e => set('physicianFax', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass} htmlFor="f00070-physician-street">Street address</label>
            <input id="f00070-physician-street" type="text" value={form.physicianStreet} onChange={e => set('physicianStreet', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass} htmlFor="f00070-physician-city">City</label>
            <input id="f00070-physician-city" type="text" value={form.physicianCity} onChange={e => set('physicianCity', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass} htmlFor="f00070-physician-state">State</label>
              <input id="f00070-physician-state" type="text" maxLength={2} value={form.physicianState} onChange={e => set('physicianState', e.target.value.toUpperCase())} readOnly={isReadOnly} className={inputClass} />
            </div>
            <div>
              <label className={labelClass} htmlFor="f00070-physician-zip">ZIP + 4</label>
              <input id="f00070-physician-zip" type="text" value={form.physicianZip} onChange={e => set('physicianZip', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Generate PDF moved to page action bar */}

      {actionError && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex justify-between items-start">
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className="ml-2 text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {/* Documents */}
      {formRecord && (
        <>
          <DocumentUpload
            documents={(formRecord.documents ?? []).map(d => ({
              id: d.id,
              name: d.name,
              size: d.size,
              uploadedAt: d.uploadedAt,
            }))}
            stagedFiles={stagedFiles}
            canUpload={formRecord.status === 'COMPLETE'}
            onFilesSelected={handleFilesSelected}
            onStagedRemove={handleRemoveStagedFile}
            onDownload={handleDownloadDocument}
            onDelete={handleDeleteDocument}
            uploading={uploading}
            deletingId={deletingDocId}
            collapsible={true}
            defaultOpen={true}
            maxSizeMb={7}
          />
          {stagedFiles.length > 0 && formRecord.status === 'COMPLETE' && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleUploadStagedFiles}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Uploading...' : 'Upload Files'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CCPPAReauthForm;
